import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAny, not, TGetValueFn } from "@components/smart/FieldInfo";
import { EmailDef, PhoneNumberDef } from "@components/smart/GeneralFieldDefinition";
import { createPath } from "@odata/BindingContext";
import {
    CompanyEntity,
    EntitySetName,
    EntityTypeName,
    PrCsszOzuspojSubmissionCompanyEntity,
    PrCsszOzuspojSubmissionContactWorkerEntity,
    PrCsszOzuspojSubmissionDetailEntity,
    PrCsszOzuspojSubmissionEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import { isOfSubmissionSubType } from "@pages/payroll/submission/Submission.utils";
import {
    addEmployeeMasterField,
    addEmployerDefinitions,
    CSSZTranslationFiles
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrCSSZSubmissionOZUSPOJFormView, {
    HasContactWorkerLocalPath,
    HasDateEndLocalPath
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionOZUSPOJFormView";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { FieldType, GroupedField } from "../../../../enums";
import { addFormDef, addFormGroup, addRowsToGroup } from "../../../../views/formView/Form.utils";

const isTermination = isOfSubmissionSubType(PrSubmissionSubtypeCode.OzuspojTermination);
const isRegistration = isOfSubmissionSubType(PrSubmissionSubtypeCode.OzuspojRegistration);

const showDateEnd: TGetValueFn<boolean> = (args) => {
    return args.storage.getValueByPath(HasDateEndLocalPath) === true;
};

const showContactWorker: TGetValueFn<boolean> = (args) => {
    return args.storage.getValueByPath(HasContactWorkerLocalPath) === true;
};

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrCSSZSubmissionOZUSPOJFormView, context,  {
        ...args,
        type: PrSubmissionTypeCode.EmployerDiscountOnSocialInsurance
    }));

    d.form.additionalProperties = [
        ...d.form.additionalProperties,
        { id: createPath(PrCsszOzuspojSubmissionEntity.Detail, PrCsszOzuspojSubmissionDetailEntity.Company, PrCsszOzuspojSubmissionCompanyEntity.Company, CompanyEntity.Id) }
    ];

    // **************** Add company / employer fields ****************
    addEmployerDefinitions(d);

    // **************** Add Employee related fields ****************
    addEmployeeMasterField(d, {
        separateEmployeeIdentifiers: true,
        withoutEmployeeContact: true,
        isSingleEmployeeView: args?.isSingleEmployeeView
    });

    // **************** OZUSPOJ detail ****************
    const DateStartPath = createPath(PrCsszOzuspojSubmissionEntity.Detail, PrCsszOzuspojSubmissionDetailEntity.DateStart);
    const DateEndPath = createPath(PrCsszOzuspojSubmissionEntity.Detail, PrCsszOzuspojSubmissionDetailEntity.DateEnd);

    // should not be possible to change submission type on saved entity. New entity is created in wizard
    // addFormDef(d, PrCsszOzuspojSubmissionEntity.SubmissionSubtype, {
    //     type: FieldType.SegmentedButton,
    //     defaultValue: PrSubmissionSubtypeCode.OzuspojRegistration,
    //     labelStatus: LabelStatus.Hidden,
    //     fieldSettings: {
    //         items: getEnumItems(EntityTypeName.PrSubmissionSubtype, [
    //             PrSubmissionSubtypeCode.OzuspojRegistration,
    //             PrSubmissionSubtypeCode.OzuspojTermination,
    //             PrSubmissionSubtypeCode.OzuspojCancellation,
    //         ])
    //     },
    //     affectedFields: [
    //         { id: DateStartPath },
    //         { id: DateEndPath },
    //         { id: HasDateEndLocalPath }
    //     ]
    // });

    addFormDef(d, HasDateEndLocalPath, {
        type: FieldType.Switch,
        label: i18next.t("Submission:OZUSPOJForm.HasDateEnd"),
        defaultValue: false,
        isVisible: not(isTermination),
        fieldSettings: {
            type: SwitchType.YesNo
        },
        customizationData: {
            dependents: [DateEndPath]
        },
        affectedFields: [{ id: DateEndPath }],
    });

    addFormDef(d, DateStartPath, {
        label: i18next.t("Submission:OZUSPOJForm.DateStart"),
        isRequired: true,
        isVisible: not(isTermination),
    });

    addFormDef(d, DateEndPath, {
        label: i18next.t("Submission:OZUSPOJForm.DateEnd"),
        isRequired: true,
        isVisible: ifAny(isTermination, showDateEnd),
    });

    addRowsToGroup(d, PrSubmissionGroup.Main, [[
        { id: DateStartPath },
        { id: HasDateEndLocalPath },
        { id: DateEndPath },
    ]]);

    // **************** Contact Worker ****************
    const pathToWorker = createPath(PrCsszOzuspojSubmissionEntity.Detail, PrCsszOzuspojSubmissionDetailEntity.ContactWorker);
    addFormDef(d, HasContactWorkerLocalPath, {
        type: FieldType.Switch,
        defaultValue: false,
        fieldSettings: {
            type: SwitchType.YesNo
        },
        affectedFields: [
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.FirstName) },
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.LastName) },
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.PhoneNumber) },
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.Email) }
        ],
    });
    addFormDef(d, createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.FirstName), {
        groupedField: GroupedField.MultiStart,
        isVisible: showContactWorker
    });
    addFormDef(d, createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.LastName), {
        groupedField: GroupedField.MultiEnd,
        isVisible: showContactWorker
    });
    addFormDef(d, createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.PhoneNumber), {
        ...PhoneNumberDef,
        isVisible: showContactWorker
    });
    addFormDef(d, createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.Email), {
        ...EmailDef,
        isVisible: showContactWorker
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.ContactWorker,
        togglePropPath: HasContactWorkerLocalPath,
        title: i18next.t("Submission:OZUSPOJForm.ContactWorker"),
        rows: [[
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.FirstName) },
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.LastName) },
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.PhoneNumber) },
            { id: createPath(pathToWorker, PrCsszOzuspojSubmissionContactWorkerEntity.Email) }
        ]]
    });

    return {
        ...d,
        entitySet: EntitySetName.PrCsszOzuspojSubmissions
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrCsszOzuspojSubmission, getDefinitions);